import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { useTranslation } from "react-i18next";
import PrimeTable from "../../PrimeTable/PrimeTable";
import { useGetFinantialInstitutions } from "../../OpenBanking/OpenBankingService";
import * as xlsx from "xlsx";
import func from "../../../services/functions";

const PreviewReferenceData = ({ visible, onHide }) => {
  const { t } = useTranslation("translations");
  const { data: financialInstitutions, loading: banksLoading } = useGetFinantialInstitutions();
  const [countriesData, setCountriesData] = useState([]);
  const [banksData, setBanksData] = useState([]);
  const [currenciesData, setCurrenciesData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && countriesData.length === 0) {
      loadAllData();
    }
  }, [visible]);

  useEffect(() => {
    if (financialInstitutions && financialInstitutions.length > 0) {
      setBanksData(
        financialInstitutions.map((bank, idx) => ({
          id: idx,
          bic: bank.bic || bank.code || "",
          bank_name: bank.name || bank.institutionName || "",
        }))
      );
    }
  }, [financialInstitutions]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const countriesResponse = await fetch("/_country_codes_type_supplier.csv");
      const countriesText = await countriesResponse.text();
      const countriesLines = countriesText.trim().split("\n").slice(2);
      const countriesCSV = countriesLines.join("\n");
      const countriesWorkbook = xlsx.read(countriesCSV, { type: "string" });
      const countriesRows = xlsx.utils.sheet_to_json(
        countriesWorkbook.Sheets[countriesWorkbook.SheetNames[0]]
      );
      setCountriesData(
        countriesRows
          .filter((row) => row["Code Value"])
          .map((row, idx) => ({
            id: idx,
            country_code: row["Code Value"] || "",
            country_name: row.Definition || "",
          }))
      );


      const currenciesResponse = await fetch("/reference_currencies.csv");
      const currenciesText = await currenciesResponse.text();
      const currenciesWorkbook = xlsx.read(currenciesText, { type: "string" });
      const currenciesRows = xlsx.utils.sheet_to_json(
        currenciesWorkbook.Sheets[currenciesWorkbook.SheetNames[0]]
      );
      setCurrenciesData(
        currenciesRows
          .filter((row) => row.currency_code) 
          .map((row, idx) => ({
            id: idx,
            currency_code: row.currency_code || "",
            currency_name: row.currency_name || "",
          }))
      );
    } catch (error) {
      console.error("Error loading reference data:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (filename) => {
    try {
      if (filename === "countries") {
        const link = document.createElement("a");
        link.href = "/_country_codes_type_supplier.csv";
        link.download = "_country_codes_type_supplier.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (filename === "banks") {
        const rows = [
          ["bic", "bank_name"],
          ...banksData.map((bank) => [bank.bic, bank.bank_name]),
        ];
        func.exportToCsv("financial_institutions.csv", rows);
      } else if (filename === "currencies") {
        const link = document.createElement("a");
        link.href = "/reference_currencies.csv";
        link.download = "reference_currencies.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading:", error);
    }
  };

  const countriesColumns = [
    {
      header: t("exports.countryCode"),
      field: "country_code",
      style: { minWidth: "120px", maxWidth: "150px" },
      sortable: true,
      filter: true,
    },
    {
      header: t("exports.countryName"),
      field: "country_name",
      style: { minWidth: "180px", maxWidth: "350px" },
      sortable: true,
      filter: true,
    },
  ];

  const banksColumns = [
    {
      header: t("exports.bicCode"),
      field: "bic",
      style: { minWidth: "140px", maxWidth: "200px" },
      sortable: true,
      filter: true,
    },
    {
      header: t("exports.bankName"),
      field: "bank_name",
      style: { minWidth: "200px", maxWidth: "350px" },
      sortable: true,
      filter: true,
    },
  ];

  const currenciesColumns = [
    {
      header: "Code",
      field: "currency_code",
      style: { minWidth: "100px", maxWidth: "150px" },
      sortable: true,
      filter: true,
    },
    {
      header: "Currency",
      field: "currency_name",
      style: { minWidth: "150px", maxWidth: "300px" },
      sortable: true,
      filter: true,
    },
  ];

  const getDownloadHref = (downloadKey) => {
    if (downloadKey === "countries") {
      return "/_country_codes_type_supplier.csv";
    } else if (downloadKey === "currencies") {
      return "/reference_currencies.csv";
    }
    return "#";
  };

  const getDownloadName = (downloadKey) => {
    if (downloadKey === "countries") {
      return "_country_codes_type_supplier.csv";
    } else if (downloadKey === "currencies") {
      return "reference_currencies.csv";
    }
    return "download.csv";
  };

const downloadButton = (downloadKey) => {
      return downloadKey === "banks" ? (
        <button
          onClick={() => downloadCSV(downloadKey)}
          className="btn btn-primary shadow-sm btn-xs text-light"
        >
          <i className="pi pi-download mr-2"></i>
          {t("exports.download")}
        </button>
      ) : (
        <a
          href={getDownloadHref(downloadKey)}
          className="btn btn-primary shadow-sm btn-xs text-light"
          download={getDownloadName(downloadKey)}
        >
          <i className="pi pi-download mr-2"></i>
          {t("exports.download")}
        </a>
)}

  const gridTemplate = (data, columns, downloadKey) => {
    const headerButtonsLeft =  downloadButton(downloadKey)
    return (<div>
      <div style={{ marginBottom: "1rem" }}>

      </div>
      <PrimeTable
        value={data}
        columns={columns}
        simpleTable={true}
        compact={true}
        showFilters={false}
        paginator={true}
        rows={10}
        showSelectColumns={false}
        filterDisplay={false}
        responsiveLayout="scroll"
        showGlobalSearch={true}
        searchableKeys={columns.map((col) => col.field)}
        loading={loading}
        dataKey="id"
        headerButtonsLeft={headerButtonsLeft}
      />
    </div>)
  };

  return (
    <Dialog
      header={t("exports.referenceData")}
      visible={visible}
      onHide={onHide}
      style={{ width: "40vw", height: "70vh", display: "flex", flexDirection: "column" }}
      breakpoints={{ "960px": "85vw", "641px": "100vw" }}
      modal
      contentStyle={{paddingBottom:0}}
      
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div style={{ flex: 1, overflow: "auto" }}>
          <TabView>
            <TabPanel header={t("exports.countryCode")}>
              {gridTemplate(countriesData, countriesColumns, "countries")}
            </TabPanel>

            <TabPanel header={t("mySuppliers.financialInstitution")}>
              {gridTemplate(banksData, banksColumns, "banks")}
            </TabPanel>

            <TabPanel header="Currency">
              {gridTemplate(currenciesData, currenciesColumns, "currencies")}
            </TabPanel>
          </TabView>
        </div>
      </div>
    </Dialog>
  );
};

export default PreviewReferenceData;
